import { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { SkinSort } from '../api/types';
import { useDebounced, useQuery } from '../hooks/useApi';
import { useScope, useScopeLabel } from '../hooks/useScope';
import { useI18n } from '../i18n';
import { SkinCard, TemplateCard } from '../components/SkinCard';
import { Pagination } from '../components/Pagination';
import { Banner, EmptyState, ErrorState, SkeletonGrid } from '../components/States';
import type { MessageKey } from '../i18n/messages';
import { itemDoGrupo, itemDoTemplate, trackSelectItem, trackViewItemList } from '../lib/tracking';

/**
 * A ordem padrão é "Mais à venda", do maior número de anúncios para o menor.
 *
 * É o que põe na frente a skin que a loja consegue entregar hoje: muita oferta
 * significa preço estável e peça disponível. Abrir por preço crescente enchia a
 * primeira tela com o que quase ninguém procura.
 */
const ORDEM_PADRAO: SkinSort = 'onsale';

const SORTS: Array<{ value: SkinSort; key: MessageKey }> = [
  { value: 'onsale', key: 'sort.onsale' },
  { value: 'price_asc', key: 'sort.priceAsc' },
  { value: 'price_desc', key: 'sort.priceDesc' },
  { value: 'discount', key: 'sort.discount' },
  { value: 'name', key: 'sort.name' },
];

export function SkinsPage() {
  const scope = useScope();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // A busca é digitada na barra de filtros e chega pela URL.
  const search = useDebounced(params.get('q') ?? '', 350);

  const sort = (params.get('sort') as SkinSort) ?? ORDEM_PADRAO;
  const page = Number(params.get('page') ?? 1);
  const grouped = params.get('grouped') !== '0';

  // Facetas controladas pelo painel de filtros, no topo.
  const exterior = params.get('exterior') ?? '';
  const rarity = params.get('rarity') ?? '';
  const quality = params.get('quality') ?? '';
  const st = params.get('st') ?? 'all';
  const team = params.get('team') ?? '';
  const color = params.get('color') ?? '';
  const minPrice = params.get('minPrice') ?? '';
  const maxPrice = params.get('maxPrice') ?? '';


  const patch = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === null || value === '') merged.delete(key);
      else merged.set(key, value);
    }
    if (!('page' in next)) merged.set('page', '1');
    setParams(merged, { replace: true });
  };

  const label = useScopeLabel(scope);

  const query = useQuery(
    () =>
      api.skins({
        ...scope.apiScope,
        search: search || undefined,
        sort,
        page,
        pageSize: 48,
        exterior: exterior || undefined,
        rarity: rarity || undefined,
        quality: quality || undefined,
        st: st !== 'all' ? st : undefined,
        team: team || undefined,
        color: color || undefined,
        minPrice: minPrice ? Number(minPrice) : undefined,
        maxPrice: maxPrice ? Number(maxPrice) : undefined,
      }),
    [scope.label, scope.isCategory, search, sort, page, exterior, rarity, quality, st, team, color, minPrice, maxPrice],
    Boolean(scope.label),
  );

  const flattened = useMemo(() => (query.data ? query.data.items.flatMap((g) => g.variants) : []), [query.data]);

  // A lista do catálogo que a pessoa está vendo. O id é o endereço da seção
  // (/all, /k/rifle, /w/weapon_ak47), estável entre idiomas; o nome é o rótulo.
  const lista = { id: scope.basePath, nome: label || scope.basePath };
  useEffect(() => {
    if (query.loading || !query.data) return;
    trackViewItemList(
      lista,
      grouped ? query.data.items.map((g, i) => itemDoGrupo(g, i)) : flattened.map((t, i) => itemDoTemplate(t, i)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, query.loading, grouped]);

  if (!scope.label) {
    return <EmptyState>{t('empty.selectSomething')}</EmptyState>;
  }

  return (
    <>
      <div className="crumbs">
        <span>{t('crumbs.market')}</span>
        <span className="sep">›</span>
        <b>{label}</b>
        <span className="count-tag">
          {query.data
            ? grouped
              ? t('level2.countGrouped', { skins: query.data.total, variants: query.data.variantsLoaded })
              : t('level2.countFlat', { n: flattened.length })
            : ''}
        </span>
      </div>

      <div className="toolbar">
        <select className="select" value={sort} onChange={(e) => patch({ sort: e.target.value })}>
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {t(s.key)}
            </option>
          ))}
        </select>
        <label className="checkbox">
          <input type="checkbox" checked={grouped} onChange={(e) => patch({ grouped: e.target.checked ? null : '0' })} />
          {t('level2.groupBySkin')}
        </label>
      </div>

      {query.data?.source === 'demo' && <Banner kind="warn">{query.data.notice}</Banner>}
      {query.error && <ErrorState error={query.error} onRetry={query.refetch} />}

      {query.loading && <SkeletonGrid />}

      {!query.loading && query.data && query.data.items.length === 0 && (
        <EmptyState>
          {search ? (
            t('empty.withSearch', { weapon: label, search })
          ) : (
            <div style={{ maxWidth: 520, margin: '0 auto' }}>
              <p style={{ marginTop: 0 }}>{t('empty.noSkins', { weapon: label })}</p>
              <p style={{ color: 'var(--muted)', fontSize: 13 }}>
                Tente outra categoria ou limpe os filtros.
              </p>
            </div>
          )}
        </EmptyState>
      )}

      {!query.loading && query.data && query.data.items.length > 0 && (
        <>
          <div className="grid">
            {grouped
              ? query.data.items.map((group, i) => (
                  <SkinCard
                    key={group.key}
                    group={group}
                    onClick={() => {
                      trackSelectItem(lista, itemDoGrupo(group, i));
                      navigate(`${scope.basePath}/s/${encodeURIComponent(group.key)}`);
                    }}
                  />
                ))
              : flattened.map((template, i) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    onClick={() => {
                      trackSelectItem(lista, itemDoTemplate(template, i));
                      navigate(`${scope.basePath}/t/${template.id}`);
                    }}
                  />
                ))}
          </div>

          <Pagination
            page={query.data.pageIndex}
            pageSize={query.data.pageSize}
            total={query.data.total}
            hasNext={query.data.hasNext}
            busy={query.loading}
            onChange={(next) => patch({ page: String(next) })}
          />
        </>
      )}
    </>
  );
}
